import { FamiliaSignupForm } from "./LandingPage";
import { FamiliaBot } from "@/components/app/FamiliaBot";
import { useEffect, useState } from "react";
import { initPixel, trackPixel } from "@/lib/metaPixel";

function StickyCta() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const alta = document.getElementById("alta");
      const altaVisible = alta && Math.abs(alta.getBoundingClientRect().top) < window.innerHeight * 0.7;
      setVisible(window.scrollY > 550 && !altaVisible);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <div data-testid="familias-sticky-cta" className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur border-t border-primary/40 px-4 py-3 pr-20">
      <a href="#alta" className="btn-volt flex items-center justify-center w-full h-11 text-sm">
        Empezar por 1€/mes
      </a>
    </div>
  );
}

const BENEFITS = [
  ["🔴", "Partido en directo", "Cronómetro con descanso y 2ª parte, cambios (sale/entra) con minutos automáticos y posición en el campo"],
  ["📈", "Evolución real", "Temporadas comparadas, tests periódicos, retos y media de su categoría"],
  ["⭐", "Momentos con foto", "El álbum deportivo de su carrera: jugadas, debut, primer gol"],
  ["🔗", "Ficha para compartir", "Un enlace para abuelos, tíos y ojeadores — sin que se registren"],
];

const STEPS = [
  ["01", "Date de alta", "Nombre, email y cuántos jugadores sigues. Pago seguro con Stripe."],
  ["02", "Crea su ficha", "Recibes tu acceso al instante: foto, dorsal, posición y a jugar."],
  ["03", "Apunta y comparte", "Cada finde registras el partido en 1 minuto y compartes su ficha."],
];

const FAQS = [
  ["¿Necesita usarlo el club de mi hijo/a?", "No. Tú registras los datos que ves desde la grada — no depende del club para nada."],
  ["¿Cuánto cuesta exactamente?", "12€ al año por jugador, IVA incluido (1€ al mes). Si sigues a 2 jugadores, 24€/año. Sin permanencia."],
  ["¿Puedo darme de baja?", "Sí, cuando quieras desde tu panel. No hay permanencia ni letra pequeña."],
  ["¿Vale para cualquier deporte?", "Está pensado para fútbol: partidos, goles, asistencias, minutos, tarjetas y evolución."],
];

export default function FamiliasPage() {
  useEffect(() => {
    initPixel();
    trackPixel("ViewContent", { content_name: "sudefootball_player_landing" });
  }, []);
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="p-5 md:p-6 flex items-center justify-between max-w-5xl mx-auto w-full border-b border-border">
        <div className="flex items-center gap-3">
          <img src="/brand/sudefootball-white-40.png" alt="Sudefootball" className="h-6 w-auto" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-primary font-black">Player</span>
        </div>
        <a href="/login" data-testid="familias-login-link" className="text-xs uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors">Ya tengo cuenta →</a>
      </header>

      <main className="flex-1 max-w-5xl mx-auto px-4 py-12 md:py-16 w-full">
        <div className="text-center">
          <div className="overline text-primary">Sudefootball Player · Para futbolistas de 6 a 18 años</div>
          <h1 className="mt-4 font-heading font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-none">
            Tu temporada. Tus datos.<br /><span className="text-primary">Tu evolución.</span>
          </h1>
          <p className="mt-5 text-sm md:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            El historial deportivo de tu hijo/a con las mismas herramientas que usan los clubes: partido en directo
            desde la grada, retos, tests, momentos con foto y una ficha profesional para compartir.
            <b className="text-foreground"> 1€ al mes por jugador (pago anual de 12€, IVA incluido).</b>
          </p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <a href="#alta" className="btn-volt inline-flex" data-testid="familias-hero-cta">Empezar por 1€/mes</a>
            <a
              href="/p/familia-demo"
              target="_blank"
              rel="noreferrer"
              data-testid="familias-demo-btn"
              className="inline-flex items-center gap-2 h-11 px-5 border border-primary/60 text-primary hover:bg-primary/10 text-xs uppercase font-heading font-black tracking-wider transition-colors"
            >
              Ver demo de la ficha →
            </a>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 text-left">
          {BENEFITS.map(([icon, title, desc]) => (
            <div key={title} className="bg-card border border-border p-4 hover:border-primary/50 transition-colors">
              <div className="text-2xl">{icon}</div>
              <div className="mt-2 font-heading font-black text-sm uppercase">{title}</div>
              <div className="mt-1 text-[11px] text-muted-foreground leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>

        <div className="mt-12 border border-primary/40 bg-primary/5 p-6 md:p-8" id="alta">
          <div className="overline text-primary text-center">Empieza hoy · Sin permanencia</div>
          <FamiliaSignupForm />
          <div className="mt-4 text-[11px] text-muted-foreground text-center">
            🔒 Pago seguro con Stripe · Sin permanencia · Cancela cuando quieras
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground text-center">
            Hasta 8 jugadores por cuenta · Precio de lanzamiento respetado 24 meses · Tras el pago recibirás tu acceso por email al instante
          </div>
        </div>

        <div className="mt-14" data-testid="familias-screens-section">
          <h2 className="font-heading font-black uppercase text-lg text-center">Así se ve por dentro</h2>
          <p className="text-center text-xs text-muted-foreground mt-1">Capturas reales de la app — sin retoques</p>
          <div className="mt-6 flex justify-center">
            <div className="w-full max-w-[280px] rounded-[1.6rem] border-4 border-zinc-700 bg-black p-1.5 shadow-2xl">
              <video
                data-testid="familias-video"
                src="/media/player_video.mp4"
                poster="/media/player_video_poster.jpg"
                controls
                playsInline
                preload="none"
                className="w-full rounded-[1.15rem]"
              />
            </div>
          </div>
          <p className="text-center text-[11px] text-muted-foreground mt-3">▶ 26 segundos: mira cómo funciona</p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              ["/landing/shot_directo.jpg", "Partido en directo", "Cronómetro, goles y acciones con el minuto exacto. Descanso y 2ª parte incluidos."],
              ["/landing/shot_posicion.jpg", "Minutos y posición", "Sale y entra del banquillo: los minutos se calculan solos. Marca su posición en el campo."],
              ["/landing/shot_ficha.jpg", "Su ficha de jugador", "Evolución, estadísticas y partidos — compártela con abuelos, familia y entrenadores."],
            ].map(([src, title, desc]) => (
              <div key={title} className="text-center">
                <div className="mx-auto w-full max-w-[240px] rounded-[1.6rem] border-4 border-zinc-700 bg-black p-1.5 shadow-2xl">
                  <img src={src} alt={title} loading="lazy" className="w-full rounded-[1.15rem]" />
                </div>
                <div className="mt-3 font-heading font-black text-sm uppercase">{title}</div>
                <div className="mt-1 text-[11px] text-muted-foreground leading-relaxed max-w-[240px] mx-auto">{desc}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <a href="#alta" className="btn-volt inline-flex">Quiero esto para mi peque · 1€/mes</a>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-heading font-black uppercase text-lg text-center">Cómo funciona</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-3">
            {STEPS.map(([n, t, d]) => (
              <div key={n} className="border border-border p-5">
                <div className="font-heading font-black text-3xl text-primary">{n}</div>
                <div className="mt-2 font-heading font-black text-sm uppercase">{t}</div>
                <div className="mt-1 text-xs text-muted-foreground leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 max-w-2xl mx-auto">
          <h2 className="font-heading font-black uppercase text-lg text-center">Preguntas frecuentes</h2>
          <div className="mt-6 divide-y divide-border border border-border">
            {FAQS.map(([q, a]) => (
              <div key={q} className="p-4">
                <div className="font-heading font-bold text-sm">{q}</div>
                <div className="mt-1 text-xs text-muted-foreground leading-relaxed">{a}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center flex items-center justify-center gap-3 flex-wrap">
            <a href="#alta" className="btn-volt inline-flex">Empezar por 1€/mes</a>
            <a href="/p/familia-demo" target="_blank" rel="noreferrer" data-testid="familias-demo-btn-bottom" className="inline-flex items-center h-11 px-5 border border-primary/60 text-primary hover:bg-primary/10 text-xs uppercase font-heading font-black tracking-wider transition-colors">Ver demo</a>
          </div>
        </div>
      </main>

      <footer className="py-6 text-center overline text-[10px] border-t border-border">
        Sudefootball · by Sudeporte · sudefootball@sudeporte.com
      </footer>
      <FamiliaBot context="landing" />
      <StickyCta />
    </div>
  );
}
